import React, { useState, useEffect } from 'react'
import {
    EyeIcon,
    ArrowLeftIcon,
    PencilIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/20/solid'
import { QrCodeIcon } from '@heroicons/react/24/outline'
import { Input, InputGroup } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { MultiSelect } from 'components/multi-select'
import { Select } from 'components/select'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline'
import { BoatIcon, ReturnTripIcon } from 'components/icons'
import { BoatBookingImage } from 'components/icons'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { GET_ALL_BOOKINGS_LIST } from 'graphql/booking/queries'
import { GET_ROUTE_TYPE } from 'graphql/route/queries'
import { GET_ROUTES_FOR_REPORTS, SEARCH_LOCATION } from 'graphql/reports/queries'
import { useLazyQuery } from '@apollo/client'
import loadingImage from '../../assets/images/boat-loading.gif'
import { t } from 'i18next'
import i18n from 'i18next'
import Flatpickr from 'react-flatpickr'
import { useNavigate, useLocation } from 'react-router-dom'
import { customDateFormat, customTimeFormat } from 'utils/common'
import moment from 'moment'
import { getPageRange } from 'utils/paginationHelper'
import { currentSessionDecoded, getAllowedRouteTypes } from 'helpers/auth-helper'
import { GET_REFUND_REQUEST_LIST } from 'graphql/refunds/queries'

const sortOptions = [
    { name: 'Most Popular', href: '#', current: true },
    { name: 'Best Rating', href: '#', current: false },
    { name: 'Newest', href: '#', current: false },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Helper function to get trip status badge properties
const getTripStatusBadge = tripStatus => {
    switch (tripStatus) {
        case 'CXL':
            return { color: 'red', text: t('Cancelled') }
        case 'ACT':
            return { color: 'emerald', text: t('Active') }
        case 'COM':
            return { color: 'blue', text: t('Completed') }
        case 'PEN':
            return { color: 'yellow', text: t('Pending') }
        case 'CNF':
            return { color: 'emerald', text: t('Confirmed') }
        case 'CHK':
            return { color: 'purple', text: t('Checked-in') }
        default:
            return { color: 'zinc', text: t('') }
    }
}

// Helper function to check if booking is Day Pass type
const isDayPassBooking = booking => {
    return booking?.booking_type?.code === 'DYP'
}

// Helper function to get travel date for a booking
const getBookingTravelDate = booking => {
    if (isDayPassBooking(booking)) {
        // For Day Pass, get travel_date from passengers array
        return booking?.passengers?.[0]?.travel_date
    }
    // For regular bookings, get from trip_segments
    return booking?.trip_segments?.[0]?.travel_date
}

// Helper function to get passengers for a booking
const getBookingPassengers = booking => {
    if (isDayPassBooking(booking)) {
        // For Day Pass, passengers are at booking level
        return booking?.passengers || []
    }
    // For regular bookings, get from trip_segments
    return booking?.trip_segments?.[0]?.passengers || []
}

// Helper function to get passenger count
const getPassengerCount = booking => {
    return getBookingPassengers(booking).length
}

// Helper function to get first passenger info
const getFirstPassenger = booking => {
    const passengers = getBookingPassengers(booking)
    return passengers[0]?.passenger || null
}
export default function ManageRefundPolicy({ isCheckInMode = false }) {
    const navigate = useNavigate()
    const location = useLocation()

    // Add isSearch state
    const [isSearch, setIsSearch] = useState(false)

    // Boat data
    const { filterObj: initialFilterObj } = location.state || {}
    const [filterObj, setFilterObj] = useState(
        initialFilterObj || {
            booking_code: '',
            from_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
            to_date: moment().format('YYYY-MM-DD'),
            from_station: '',
            to_station: '',
            trip_name: '',
            booked_on: '',
            booked_by: '',
        }
    )
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    const [bookingList, setBookingList] = useState(null)
    const [bookingCount, setBookingCount] = useState(0)
    const [searchBookingStatus, setSearchBookingStatus] = useState('ACT')

    // Route type filtering
    const [routeTypeList, setRouteTypeList] = useState([])
    const [selectedRouteType, setSelectedRouteType] = useState('ALL')
    const [sessionData, setSessionData] = useState(null)
    const [allowedRouteType, setAllowedRouteType] = useState('ALL') // 'ALL', 'INL', or 'DMC'

    // Routes, Trips, and Locations filtering
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedRouteIds, setSelectedRouteIds] = useState([])
    const [selectedTripIds, setSelectedTripIds] = useState([])
    const [startLocationId, setStartLocationId] = useState('')
    const [endLocationId, setEndLocationId] = useState('')

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(bookingCount / rowsPerPage)

    const [paginationGroup, setPaginationGroup] = useState(0)
    const pagesPerGroup = 10

    const handleGroupChange = newGroup => {
        setPaginationGroup(newGroup)
    }

    const handlePrevious = () => {
        if (page > 0) {
            const newPage = page - 1
            handlePageChange(newPage)
        }
    }

    const handleNext = () => {
        if (page < totalPages - 1) {
            const newPage = page + 1
            handlePageChange(newPage)
        }
    }

    useEffect(() => {
        if (initialFilterObj) {
            setFilterObj(initialFilterObj)
            setPage(0)
            setRowsPerPage(10)
            // Trigger search when returning from details page
            if (location.state?.filterObj) {
                setIsSearch(true)
                searchBookings() // Remove setTimeout and directly call searchBookings
            }
        } else {
            // Auto-search with current date range when page loads for the first time
            setIsSearch(true)
            searchBookings(0)
        }
    }, [initialFilterObj])

    const [getBookingList, { loading: bookingListLoading, error: bookingListError }] = useLazyQuery(
        GET_REFUND_REQUEST_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getRefundRequest.success && data.getRefundRequest.code === '200') {
                    setBookingList(data.getRefundRequest.refund_requests)
                    setBookingCount(data.getRefundRequest.total_count)
                }
            },
        }
    )

    const [getRouteTypeList, { loading: routeTypeListLoading }] = useLazyQuery(GET_ROUTE_TYPE, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.routeType?.length > 0) {
                setRouteTypeList(data.routeType)
            }
        },
    })

    // Get routes query
    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
    })

    // Get locations query
    const [searchLocation] = useLazyQuery(SEARCH_LOCATION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchLocation.success && data.searchLocation.code === '200') {
                setLocations(data.searchLocation.locations)
            }
        },
    })

    // Fetch session data and determine allowed route types on mount
    useEffect(() => {
        const fetchSessionData = async () => {
            try {
                const session = await currentSessionDecoded()
                if (session) {
                    setSessionData(session)
                    const allowed = getAllowedRouteTypes(session)
                    setAllowedRouteType(allowed)
                }
            } catch (error) {
                console.error('Error fetching session:', error)
            }
        }
        fetchSessionData()
        getRouteTypeList({ variables: { code: null } })
        // Fetch routes and locations
        getRoutes({ variables: { page: { offset: 0, limit: 100 }, statusCode: 'ACT' } })
        searchLocation({ variables: { page: { offset: 0, limit: 100 }, searchString: '' } })
    }, [])

    // When routes are selected, collect trips from all selected routes
    useEffect(() => {
        if (selectedRouteIds?.length > 0) {
            const selectedRoutes = routes.filter(r => selectedRouteIds.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter(
                (trip, index, self) => index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
            setSelectedTripIds([])
        }
    }, [selectedRouteIds, routes])

    // Filter route types based on user role
    const getFilteredRouteTypes = () => {
        if (!routeTypeList || routeTypeList.length === 0) return []

        if (allowedRouteType === 'ALL') {
            return routeTypeList
        }

        // Filter to only show allowed route type
        return routeTypeList.filter(type => type.code === allowedRouteType)
    }

    const filteredRouteTypes = getFilteredRouteTypes()

    // No client-side filtering needed - API handles route_type filtering

    const handleClearFilter = () => {
        setFilterObj({
            booking_code: '',
            from_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
            to_date: moment().format('YYYY-MM-DD'),
            from_station: '',
            to_station: '',
            trip_name: '',
            booked_on: '',
            booked_by: '',
        })
        setSelectedRouteType('ALL')
        setSelectedRouteIds([])
        setSelectedTripIds([])
        setStartLocationId('')
        setEndLocationId('')
        setTrips([])
        setIsSearch(true)
        setBookingList(null)
        setPage(0)
        setPaginationGroup(0)
        // Automatically call API with current date range after clearing
        searchBookings(0)
    }

    // Get the route type to pass to API
    const getRouteTypeForApi = () => {
        // If user has restricted access (not 'ALL'), use their allowed route type
        if (allowedRouteType && allowedRouteType !== 'ALL') {
            return allowedRouteType
        }
        // If 'ALL' is selected, return null to fetch all route types
        if (selectedRouteType === 'ALL') {
            return null
        }
        // Use selected route type
        return selectedRouteType
    }

    // Search bookings Booking List
    const searchBookings = (currentPage = page) => {
        const currentDate = moment()
        const oneMonthAgo = moment().subtract(1, 'months')
        const offset = currentPage * rowsPerPage // Calculate correct offset based on current page
        const routeType = getRouteTypeForApi()

        // Build trip_location object only if both locations are selected (API requires non-null values)
        const tripLocation =
            startLocationId && endLocationId
                ? {
                      start_location_id: startLocationId,
                      end_location_id: endLocationId,
                  }
                : null

        getBookingList({
            variables: {
                request_filter: {
                    booking_id: null,
                    search_string: filterObj.booking_code ? filterObj.booking_code : null,
                    status: null,
                    request_from_date: filterObj.from_date ? filterObj.from_date : oneMonthAgo.format('YYYY-MM-DD'),
                    request_to_date: filterObj.to_date ? filterObj.to_date : currentDate.format('YYYY-MM-DD'),
                    booking_type: "STD",
                    route_type: routeType,
                    route_ids: selectedRouteIds?.length > 0 ? selectedRouteIds : [],
                    trip_ids: selectedTripIds?.length > 0 ? selectedTripIds : [],
                    // trip_location: tripLocation,
                    travel_from_date: null,
                    travel_to_date: null                    

                },
                page: {
                    limit: rowsPerPage,
                    offset: offset, // Use calculated offset
                },
            },
        })
    }

    const handlePageChange = newPage => {
        setPage(newPage)
        setPaginationGroup(Math.floor(newPage / pagesPerGroup))
        searchBookings(newPage) // Pass the new page
    }

    const onClickSearch = () => {
        if (!filterObj.from_date || !filterObj.to_date) {
            setErrorMessage(t('Date range is mandatory'))
            setShowErrDialog(true)
            return
        }

        setPage(0)
        setPaginationGroup(0)
        setRowsPerPage(10)
        setIsSearch(true)
        searchBookings(0) // Pass initial page
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }
    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    const GoDetailsPage = (bookingId, travel_date) => {
        if (isCheckInMode) {
            navigate(`/manage-boat-booking/details/${bookingId}/${travel_date}`, {
                state: { filterObj, isCheckInMode: true },
            })
        } else {
            navigate(`/manage-boat-booking/details/${bookingId}/${travel_date}`, { state: { filterObj } })
        }
    }

    const handleCheckIn = booking => {
        // Navigate to check-in page with booking information
        navigate('/check-in/process', {
            state: {
                bookingInfo: booking,
                bookingId: booking.booking_id,
                travelDate: booking?.trip_segments?.[0]?.travel_date,
                segmentId: booking?.trip_segments?.[0]?.segment_id,
            },
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                    <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                </div>
                                <div>
                                    <span className="ms-3">
                                        {isCheckInMode ? t('Check-in') : t('Manage Boat Booking')}
                                    </span>
                                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                        {isCheckInMode
                                            ? t(
                                                  'Process passenger check-ins, verify bookings, and manage boarding operations'
                                              )
                                            : t(
                                                  'Efficiently manage reservations, modify bookings, and update customer details for seamless operations'
                                              )}
                                    </p>
                                </div>
                            </h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                            {isCheckInMode && (
                                <button
                                    onClick={() => navigate('/check-in/qr-scanner')}
                                    className="inline-flex items-center gap-x-2 hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-4 py-2 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    <QrCodeIcon className="h-5 w-5" />
                                    {t('Scan QR Code')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                <div className="sm:col-span-2 lg:col-span-2">
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Search')}
                    </label>
                    <InputGroup>
                        <MagnifyingGlassIcon />
                        <input
                            id="booking_code"
                            name="booking_code"
                            type="text"
                            placeholder={t('Booking code, Name, Mobile, Email')}
                            value={filterObj.booking_code}
                            onChange={e => {
                                setFilterObj({ ...filterObj, booking_code: e.target.value })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </InputGroup>
                </div>
                {/* Route Type Dropdown */}
                <div>
                    <label htmlFor="route_type" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Route Type')}
                    </label>
                    <select
                        id="route_type"
                        name="route_type"
                        value={selectedRouteType}
                        onChange={e => {
                            setSelectedRouteType(e.target.value)
                            setSelectedRouteIds([])
                            setSelectedTripIds([])
                        }}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    >
                        {allowedRouteType === 'ALL' && <option value="ALL">{t('All')}</option>}
                        {filteredRouteTypes?.map(routeType => (
                            <option key={routeType.code} value={routeType.code}>
                                {routeType.description.en}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Travel Date Range')} <span className="text-red/100">*</span>
                    </label>
                    <div>
                        <Flatpickr
                            options={{
                                mode: 'range',
                                dateFormat: 'Y-m-d',
                            }}
                            placeholder={t('From - To')}
                            value={[filterObj.from_date, filterObj.to_date]}
                            onChange={date => {
                                setFilterObj({
                                    ...filterObj,
                                    from_date: date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                    to_date: date[1] ? date[1].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                {/* Route Multi-Select */}
                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('Route')}</label>
                    <MultiSelect
                        placeholder={t('All')}
                        value={selectedRouteIds}
                        onChange={values => {
                            setSelectedRouteIds(values)
                            setSelectedTripIds([])
                        }}
                        options={routes
                            .filter(route => {
                                if (selectedRouteType === 'ALL') return true
                                return route.master_route?.route_type?.code === selectedRouteType
                            })
                            .map(route => ({
                                value: route.route_id,
                                label: i18n.language === 'ar' ? route.route_name.ar : route.route_name.en,
                            }))}
                    />
                </div>
                {/* Trip Multi-Select */}
                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('Trip')}</label>
                    <MultiSelect
                        placeholder={t('All')}
                        value={selectedTripIds}
                        onChange={values => setSelectedTripIds(values)}
                        options={trips.map(trip => ({
                            value: trip.trip_id,
                            label: i18n.language === 'ar' ? trip.trip_name.ar : trip.trip_name.en,
                        }))}
                        disabled={selectedRouteIds?.length === 0}
                    />
                </div>
                {/* From Location */}
                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('From Location')}</label>
                    <Select value={startLocationId} onChange={e => setStartLocationId(e.target.value)}>
                        <option value="">{t('All')}</option>
                        {locations.map(location => (
                            <option key={location.id} value={location.id}>
                                {i18n.language === 'ar' ? location.name.ar : location.name.en}
                            </option>
                        ))}
                    </Select>
                </div>
                {/* To Location */}
                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('To Location')}</label>
                    <Select value={endLocationId} onChange={e => setEndLocationId(e.target.value)}>
                        <option value="">{t('All')}</option>
                        {locations.map(location => (
                            <option key={location.id} value={location.id}>
                                {i18n.language === 'ar' ? location.name.ar : location.name.en}
                            </option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Request From Date')} <span className="text-red/100">*</span>
                    </label>
                    <div>
                        <Flatpickr
                            options={{
                                dateFormat: 'Y-m-d',
                            }}
                            placeholder={t('From - To')}
                            value={[filterObj.request_from_date]}
                            onChange={date => {
                                setFilterObj({
                                    ...filterObj,
                                    request_from_date: date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="region" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Request To Date')} <span className="text-red/100">*</span>
                    </label>
                    <div>
                        <Flatpickr
                            options={{
                                mode: 'range',
                                dateFormat: 'Y-m-d',
                            }}
                            placeholder={t('From - To')}
                            value={[filterObj.from_date, filterObj.to_date]}
                            onChange={date => {
                                setFilterObj({
                                    ...filterObj,
                                    from_date: date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                    to_date: date[1] ? date[1].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="flex gap-2 items-end">
                    <button
                        type="button"
                        onClick={onClickSearch}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                    >
                        {t('Search')}
                    </button>
                    <button
                        type="button"
                        onClick={handleClearFilter}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Clear')}
                    </button>
                </div>
            </div>
            {!isSearch && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Search booking ')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('Try adjusting your search criteria and click search Button')}
                    </p>
                    <div className="mt-6"></div>
                </div>
            )}
            {/* Booking List */}
            {bookingListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {bookingListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No trips found')}</div>
                </div>
            )}
            {!bookingListLoading && !bookingListError && isSearch && (!bookingList || bookingList?.length === 0) && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {isSearch && !bookingListLoading && !bookingListError && bookingList?.length > 0 && (
                <div>
                    <div className="bg-white">
                        {bookingList
                            .filter(
                                booking =>
                                    !isCheckInMode ||
                                    booking?.trip_segments?.[0]?.route?.master_route?.route_type?.code !== 'DMC'
                            )
                            .map(booking => (
                                <div key={booking.booking_id} className="mt-5">
                                    {/* Refund Request Info */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-t-lg px-4 py-2 mb-0 flex flex-wrap gap-6 items-center">
                                        <div>
                                            <span className="text-xs text-gray-500">{t('Refund Request ID')}</span>
                                            <div className="font-semibold text-blue-900">{booking.request_id || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500">{t('Refund Code')}</span>
                                            <div className="font-semibold text-blue-900">{booking.request_code || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500">{t('Request Date')}</span>
                                            <div className="font-semibold text-blue-900">{customDateFormat(booking.request_date) || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500">{t('Refund Status')}</span>
                                            <div className="font-semibold text-blue-900">{booking.status || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="text-xs text-gray-500">{t('Refund Amount')}</span>
                                            <div className="font-semibold text-blue-900">
                                                {/* Show total refund amount from refund_details if available */}
                                                {Array.isArray(booking.refund_details) && booking.refund_details.length > 0
                                                    ? booking.refund_details.reduce((sum, d) => sum + (Number(d.refund_amount) || 0), 0).toFixed(3) + ' BHD'
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                    <h2 className="sr-only">{t('Recent orders')}</h2>
                                    <div className="w-full grid grid-cols-1 gap-4">
                                        <div className="max-w-full space-y-8 sm:px-4 lg:max-w-full lg:px-0">
                                            <div className="border-b border-t border-gray-200 shadow-sm sm:rounded-lg sm:border bg-slate-50">
                                                <div className="relative items-center border-b border-gray-200 px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-y-3 p-2 pb-0">
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('Booking Code')}</dd>
                                                        <dt className="font-medium text-gray-900 overflow-ellipsis">
                                                            {booking.booking_code || 'N/A'}
                                                        </dt>
                                                    </div>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('Travel Date')}</dd>
                                                        <dt className="font-medium text-gray-900 flex items-center gap-2">
                                                            {customDateFormat(getBookingTravelDate(booking))}
                                                            {booking?.trip_segments?.[0]?.trip_status && (
                                                                <Badge
                                                                    color={
                                                                        getTripStatusBadge(
                                                                            booking?.trip_segments?.[0]?.trip_status
                                                                        ).color
                                                                    }
                                                                    className="text-xs"
                                                                >
                                                                    {
                                                                        getTripStatusBadge(
                                                                            booking?.trip_segments?.[0]?.trip_status
                                                                        ).text
                                                                    }
                                                                </Badge>
                                                            )}
                                                        </dt>
                                                    </div>
                                                    {!isDayPassBooking(booking) && (
                                                        <>
                                                            <div>
                                                                <dd className="mt-1 text-gray-500">
                                                                    {t('From Station')}
                                                                </dd>
                                                                <dt className="font-medium text-gray-900">
                                                                    {booking?.trip_segments?.[0]?.start?.location?.name
                                                                        ?.en || t('N/A')}
                                                                    {booking?.trip_segments?.[0]?.start
                                                                        ?.departure_time && (
                                                                        <span className="text-gray-600 text-[13px] ps-2">
                                                                            {customTimeFormat(
                                                                                booking.trip_segments[0].start
                                                                                    .departure_time
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </dt>
                                                            </div>

                                                            <div>
                                                                <dd className="mt-1 text-gray-500">
                                                                    {t('To Station')}
                                                                </dd>
                                                                <dt className="font-medium text-gray-900">
                                                                    {booking?.trip_segments?.length > 0
                                                                        ? booking.trip_segments[0]?.end?.location?.name
                                                                              ?.en || 'N/A'
                                                                        : 'N/A'}

                                                                    {booking?.trip_segments?.length > 0 &&
                                                                        booking.trip_segments[0]?.end?.arrival_time && (
                                                                            <span className="text-gray-600 text-[13px] ps-2">
                                                                                {customTimeFormat(
                                                                                    booking.trip_segments[0].end
                                                                                        .arrival_time
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                </dt>
                                                            </div>
                                                        </>
                                                    )}
                                                    {isDayPassBooking(booking) && (
                                                        <>
                                                            <div>
                                                                <dd className="mt-1 text-gray-500">
                                                                    {t('Passengers')}
                                                                </dd>
                                                                <dt className="font-medium text-gray-900">
                                                                    {getPassengerCount(booking)} {t('Passengers')}
                                                                </dt>
                                                            </div>
                                                            <div>
                                                                <dd className="mt-1 text-gray-500">{t('Seat Type')}</dd>
                                                                <dt className="font-medium text-gray-900">
                                                                    {booking?.passengers?.[0]?.seat_info
                                                                        ?.seat_type_code || 'N/A'}
                                                                </dt>
                                                            </div>
                                                        </>
                                                    )}
                                                    <div className="text-end md:col-span-2 xl:col-span-1 flex items-end flex-col gap-y-1">
                                                        {(booking?.booking_type || booking?.metadata) && (
                                                            <p className="text-primary whitespace-nowrap bg-primary/20 absolute top-0 end-3 text-sm italic font font-semibold text-center rounded-b-md w-fit p-2 px-3 pb-1 pt-1">
                                                                {
                                                                    booking.booking_type?.description[
                                                                        i18n?.language || 'en'
                                                                    ]
                                                                }
                                                                {booking.metadata?.booking_type?.description
                                                                    ? ' with ' +
                                                                      booking?.metadata?.booking_type.description[
                                                                          i18n?.language || 'en'
                                                                      ]
                                                                    : ''}
                                                            </p>
                                                        )}

                                                        <p className="font-extrabold mt-7 flex justify-normal items-center gap-x-2 text-[20px] text-primary mb-2">
                                                            <dd className="mt-1 text-gray-500 text-sm font-[400]">
                                                                {t('Ticket Cost')}
                                                            </dd>{' '}
                                                            {booking.total_price} BHD
                                                        </p>
                                                    </div>
                                                    <dl className="grid flex-1 grid-cols-2 gap-x-6 text-sm sm:col-span-3 sm:grid-cols-3 lg:col-span-2"></dl>
                                                </div>

                                                {/* Return Trip Information */}
                                                {booking?.trip_segments && booking.trip_segments.length > 1 && (
                                                    <div className="relative items-center border-b border-gray-200 px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-y-3 p-2 pb-0 bg-blue-50">
                                                        <div className="flex items-center gap-2">
                                                            {/* <ReturnTripIcon className="w-5 h-5 text-blue-600" />
                                                        <span className="text-sm font-medium text-blue-600">{t('Return Trip')}</span> */}
                                                        </div>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('Travel Date')}</dd>
                                                            <dt className="font-medium text-gray-900 flex items-center gap-2">
                                                                {customDateFormat(
                                                                    booking?.trip_segments?.[1]?.travel_date
                                                                )}
                                                                {booking?.trip_segments?.[1]?.trip_status && (
                                                                    <Badge
                                                                        color={
                                                                            getTripStatusBadge(
                                                                                booking?.trip_segments?.[1]?.trip_status
                                                                            ).color
                                                                        }
                                                                        className="text-xs"
                                                                    >
                                                                        {
                                                                            getTripStatusBadge(
                                                                                booking?.trip_segments?.[1]?.trip_status
                                                                            ).text
                                                                        }
                                                                    </Badge>
                                                                )}
                                                            </dt>
                                                        </div>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('From Station')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {booking?.trip_segments[1]?.start?.location?.name?.en ||
                                                                    'N/A'}
                                                                {booking?.trip_segments[1] && (
                                                                    <span className="text-gray-600 text-[13px] ps-2">
                                                                        {customTimeFormat(
                                                                            booking?.trip_segments[1]?.start
                                                                                ?.departure_time
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </dt>
                                                        </div>
                                                        <div>
                                                            <dd className="mt-1 text-gray-500">{t('To Station')}</dd>
                                                            <dt className="font-medium text-gray-900">
                                                                {booking?.trip_segments[1]?.end?.location?.name?.en ||
                                                                    'N/A'}
                                                                {booking?.trip_segments[1] && (
                                                                    <span className="text-gray-600 text-[13px] ps-2">
                                                                        {customTimeFormat(
                                                                            booking?.trip_segments[1]?.end?.arrival_time
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </dt>
                                                        </div>
                                                        <div className="text-end md:col-span-2 xl:col-span-1 flex items-end flex-col gap-y-1">
                                                            <div className="flex items-center gap-2 mt-7">
                                                                <BoatBookingImage className="w-[30px] h-[30px] fill-blue-600" />
                                                                <div className="text-left">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {booking?.trip_segments[1]?.route?.boat?.name
                                                                            ?.en || 'N/A'}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {booking?.trip_segments[1]?.trip?.trip_name
                                                                            ?.en || 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                <h4 className="sr-only">Items</h4>
                                                <ul className="divide-y divide-gray-200">
                                                    <li className="p-4">
                                                        <div className="flex sm:items-start">
                                                            <div className="flex-1 text-sm">
                                                                <div className="text-gray-900 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                    <div className="shrink-0 ">
                                                                        <div className="flex items-center">
                                                                            <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74] mt-3 rounded-full bg-primary" />
                                                                            <div className="ps-3 pt-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    <h5 className="text-[18px]">
                                                                                        {isDayPassBooking(booking)
                                                                                            ? t('Day Pass')
                                                                                            : booking?.trip_segments
                                                                                              ? booking
                                                                                                    ?.trip_segments[0]
                                                                                                    ?.route?.boat?.name
                                                                                                    ?.en
                                                                                              : 'N/A'}
                                                                                    </h5>
                                                                                </div>
                                                                                <p className="text-[14px] border-gray-300 sm:mt-2">
                                                                                    {isDayPassBooking(booking) ? (
                                                                                        <>
                                                                                            {getPassengerCount(booking)}{' '}
                                                                                            {t('Passengers')}
                                                                                            {booking?.passengers?.filter(
                                                                                                p =>
                                                                                                    p.passenger
                                                                                                        ?.special_needs
                                                                                            ).length > 0 && (
                                                                                                <span className="text-orange-600 font-medium">
                                                                                                    {' '}
                                                                                                    |{' '}
                                                                                                    {
                                                                                                        booking?.passengers?.filter(
                                                                                                            p =>
                                                                                                                p
                                                                                                                    .passenger
                                                                                                                    ?.special_needs
                                                                                                        ).length
                                                                                                    }{' '}
                                                                                                    {t('Special Needs')}
                                                                                                </span>
                                                                                            )}
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            {booking?.trip_segments
                                                                                                ? booking
                                                                                                      ?.trip_segments[0]
                                                                                                      ?.trip?.trip_name
                                                                                                      ?.en
                                                                                                : 'N/A'}{' '}
                                                                                            |{' '}
                                                                                            {getPassengerCount(booking)}{' '}
                                                                                            {t('Passengers')}
                                                                                            {/* Check-in status indicator */}
                                                                                            {booking?.trip_segments?.[0]
                                                                                                ?.passengers && (
                                                                                                <span className="text-sm ml-2">
                                                                                                    {(() => {
                                                                                                        const passengers =
                                                                                                            booking
                                                                                                                .trip_segments[0]
                                                                                                                .passengers
                                                                                                        const checkedInCount =
                                                                                                            passengers.filter(
                                                                                                                p =>
                                                                                                                    p
                                                                                                                        .check_in
                                                                                                                        ?.status
                                                                                                                        ?.code ===
                                                                                                                    'CHK'
                                                                                                            ).length
                                                                                                        const inProgressCount =
                                                                                                            passengers.filter(
                                                                                                                p =>
                                                                                                                    p
                                                                                                                        .check_in
                                                                                                                        ?.status
                                                                                                                        ?.code ===
                                                                                                                    'CRT'
                                                                                                            ).length

                                                                                                        if (
                                                                                                            checkedInCount >
                                                                                                            0
                                                                                                        ) {
                                                                                                            return (
                                                                                                                <span className="text-green-600 font-medium">
                                                                                                                    |{' '}
                                                                                                                    {
                                                                                                                        checkedInCount
                                                                                                                    }{' '}
                                                                                                                    {t(
                                                                                                                        'Checked-in'
                                                                                                                    )}
                                                                                                                </span>
                                                                                                            )
                                                                                                        } else if (
                                                                                                            inProgressCount >
                                                                                                            0
                                                                                                        ) {
                                                                                                            return (
                                                                                                                <span className="text-yellow-600 font-medium">
                                                                                                                    |{' '}
                                                                                                                    {
                                                                                                                        inProgressCount
                                                                                                                    }{' '}
                                                                                                                    {t(
                                                                                                                        'In Progress'
                                                                                                                    )}
                                                                                                                </span>
                                                                                                            )
                                                                                                        }
                                                                                                        return null
                                                                                                    })()}
                                                                                                </span>
                                                                                            )}
                                                                                            {booking?.trip_segments &&
                                                                                                booking
                                                                                                    ?.trip_segments[0]
                                                                                                    ?.trip_meals_summary &&
                                                                                                booking
                                                                                                    ?.trip_segments[0]
                                                                                                    ?.trip_meals_summary
                                                                                                    .length > 0 && (
                                                                                                    <span className="text-primary font-medium">
                                                                                                        {' '}
                                                                                                        |{' '}
                                                                                                        {
                                                                                                            booking
                                                                                                                ?.trip_segments[0]
                                                                                                                ?.trip_meals_summary
                                                                                                                .length
                                                                                                        }{' '}
                                                                                                        {t('Meals')}
                                                                                                    </span>
                                                                                                )}
                                                                                        </>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex shrink-0 md:place-content-end lg:place-content-center">
                                                                        <div className="flex items-center ">
                                                                            <CalendarIcon
                                                                                aria-hidden="true"
                                                                                className="h-5 w-5 text-green-500"
                                                                            />
                                                                            <p className="ml-2 text-sm font-100 text-gray-500">
                                                                                {t('Booked on')} <br /> {''}
                                                                                <span className="font-medium text-gray-700">
                                                                                    {customDateFormat(
                                                                                        booking.user_audit.created_date
                                                                                    )}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center ms-4 border-s-[1px] ps-3 border-gray-300 border-solid">
                                                                            <UserIcon
                                                                                aria-hidden="true"
                                                                                className="h-5 w-5 text-green-500"
                                                                            />
                                                                            <p className="ml-2 text-sm font-100 text-gray-500">
                                                                                {t('Booked by')} <br /> {''}
                                                                                <span className="font-medium text-gray-700">
                                                                                    {getFirstPassenger(booking)
                                                                                        ?.family_name || 'N/A'}{' '}
                                                                                    {getFirstPassenger(booking)
                                                                                        ?.given_name || ''}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex flex-col md:col-span-2 xl:col-span-1 md:place-content-end lg:items-end lg:place-items-end">
                                                                        <Badge
                                                                            color={
                                                                                booking.status?.description?.en ===
                                                                                'Confirmed'
                                                                                    ? 'emerald'
                                                                                    : booking.status?.description
                                                                                            ?.en ===
                                                                                        'Payment Processing'
                                                                                      ? 'lime'
                                                                                      : booking.status?.description
                                                                                              ?.en === 'Cancelled'
                                                                                        ? 'red'
                                                                                        : 'zinc'
                                                                            }
                                                                            className="text-end w-fit mb-3"
                                                                        >
                                                                            {booking.status?.description?.en}
                                                                        </Badge>
                                                                        <div className="flex flex-col gap-2 lg:w-[250px]">
                                                                            <button
                                                                                className="bg-primary text-white hover:bg-primary/[0.9] py-2 px-8 rounded flex-none"
                                                                                onClick={() =>
                                                                                    GoDetailsPage(
                                                                                        booking.booking_id,
                                                                                        getBookingTravelDate(booking)
                                                                                    )
                                                                                }
                                                                            >
                                                                                {t('View details')}
                                                                            </button>
                                                                            {/* Check-in button - show if allow_check_in is true for any segment and isCheckInMode is true */}
                                                                            {isCheckInMode &&
                                                                                (booking?.trip_segments?.some(
                                                                                    segment => segment.allow_check_in
                                                                                ) ||
                                                                                    booking?.trip_segments?.some(
                                                                                        segment =>
                                                                                            !segment.allow_check_in &&
                                                                                            segment.passengers?.some(
                                                                                                p =>
                                                                                                    p.check_in?.status
                                                                                                        ?.code === 'CHK'
                                                                                            )
                                                                                    )) &&
                                                                                booking?.trip_segments?.[0]?.route
                                                                                    ?.master_route?.route_type?.code !==
                                                                                    'DMC' &&
                                                                                (() => {
                                                                                    // Check if any passenger in any segment is checked in
                                                                                    const hasCheckedInPassengers =
                                                                                        booking?.trip_segments?.some(
                                                                                            segment =>
                                                                                                segment.passengers?.some(
                                                                                                    p =>
                                                                                                        p.check_in
                                                                                                            ?.status
                                                                                                            ?.code ===
                                                                                                        'CHK'
                                                                                                )
                                                                                        )

                                                                                    return (
                                                                                        <button
                                                                                            className="text-white hover:opacity-90 py-2 px-8 rounded flex-none"
                                                                                            style={{
                                                                                                backgroundColor:
                                                                                                    'rgb(40 95 116 / var(--tw-bg-opacity, 1))',
                                                                                            }}
                                                                                            onClick={() =>
                                                                                                handleCheckIn(booking)
                                                                                            }
                                                                                        >
                                                                                            {hasCheckedInPassengers
                                                                                                ? t('View Check-In')
                                                                                                : t('Check-In')}
                                                                                        </button>
                                                                                    )
                                                                                })()}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious onClick={handlePrevious} disabled={page === 0} />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext onClick={handleNext} disabled={page >= totalPages - 1} />
                    </Pagination>
                    <Divider />
                </div>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}
        </>
    )
}
